import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { get } from "@/lib/api";
import { PageHeader, SkeletonRows } from "@/components/app/Common";
import { ImportUploadStep } from "@/pages/candidates/import/ImportUploadStep";
import { ImportPreview } from "@/pages/candidates/import/ImportPreview";
import { ImportSummary } from "@/pages/candidates/import/ImportSummary";
import { ImportRowsTable } from "@/pages/candidates/import/ImportRowsTable";

const STEPS = ["Choose job and files", "Check rows", "Import", "Summary"];

function Stepper({ at }) {
  return (
    <ol className="flex flex-wrap gap-2" data-tour="pt-import-stepper" data-testid="import-stepper">
      {STEPS.map((s, i) => (
        <li key={s} aria-current={i === at ? "step" : undefined} className={`flex items-center gap-2 rounded-full px-3 py-1 text-xs ${i === at ? "bg-brand text-white" : i < at ? "bg-brand-tint text-brand" : "bg-subtle text-ink-muted"}`}>
          <span className="tabular-nums">{i + 1}</span>{s}
        </li>
      ))}
    </ol>
  );
}

function Existing({ id }) {
  const { data: imp, isLoading } = useQuery({ queryKey: ["import", id], queryFn: () => get(`/intake/imports/${id}`),
    refetchInterval: (q) => (["completed", "preview"].includes(q.state.data?.phase) ? false : 1500) });
  if (isLoading || !imp) return <SkeletonRows rows={6} />;
  const at = { uploading: 1, staging: 1, preview: 1, committing: 2, completed: 3 }[imp.phase];
  return (
    <div className="space-y-6">
      <Stepper at={at} />
      <p className="text-sm text-ink-secondary" data-tour="pt-import-meta" data-testid="import-meta">Target: <strong className="text-ink">{imp.job_title || "Talent pool (no job)"}</strong> · {imp.file_names.join(", ")}</p>
      {imp.phase === "committing" && (
        <section className="panel space-y-3 p-5" data-testid="import-committing">
          <p className="flex items-center gap-2 text-sm font-medium"><Loader2 size={14} className="animate-spin" aria-hidden="true" /> {imp.commit_progress?.progress_message || "Importing…"}</p>
          <Progress value={imp.commit_progress?.progress || 5} className="h-2 bg-brand-tint" />
        </section>
      )}
      {imp.phase === "completed" ? (
        <>
          <ImportSummary summary={imp.summary} errorsUrl={`/intake/imports/${id}/errors.csv`} errorsName={`import-errors-${id.slice(0, 8)}.csv`}>
            <Button variant="ghost" asChild><Link to="/app/candidates" data-testid="import-go-candidates">Go to candidates</Link></Button>
          </ImportSummary>
          <ImportRowsTable rows={imp.rows} committed />
        </>
      ) : imp.phase !== "committing" && <ImportPreview imp={imp} />}
    </div>
  );
}

export default function ImportWizard() {
  const { id } = useParams();
  const nav = useNavigate();
  return (
    <div className="space-y-6" data-tour="pt-import-wizard" data-testid="import-wizard">
      <Link to="/app/candidates" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink"><ArrowLeft size={12} aria-hidden="true" /> Candidates</Link>
      <PageHeader title="Bulk import" subtitle="Upload resumes, a ZIP or a spreadsheet. Nothing is saved until you review the preview and press Import."
        actions={<Button variant="ghost" asChild><Link to="/app/candidates/imports" data-tour="pt-import-history-btn" data-testid="import-history-btn">Import history</Link></Button>} />
      {id ? <Existing id={id} /> : <><Stepper at={0} /><ImportUploadStep onStarted={(newId) => nav(`/app/candidates/import/${newId}`, { replace: true })} /></>}
    </div>
  );
}
